import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Card, Grid, Tooltip, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import {
  GridColumnMenu,
  GridColumnMenuProps,
  GridPinnedColumnFields
} from "@mui/x-data-grid";
import Spinner from "components/Spinner";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import FormDialog from "components/Dialog/modal-popup";
import AddTargetForm from "./AddTargetForm";
import { _getLocation } from "middlewares/LocationApi/location";
import { GHGTargetGridProps } from "./data/GHGTartgetGrid.type";
import {
  _deleteGHGTarget,
  _downloadGHGTargetTemplate,
  _getCustomGHGTargetsByLocationsId,
  _getGHGTargetsByTargetNumber,
} from "middlewares/TargetformApi/targetform";
import { GHGTarget } from "./data/GHGTarget";
import { initialCategories } from "./data/TargetFormData";
import { Scope3GHGReductionModel } from "./data/Scope3GHGReduction";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import SnackBar from "components/SnackBar";
import AlertDialog from "components/AlertDialog";
import GuidanceBanner from "components/GuidanceBanner/GuidanceBanner";
import {
  ConfirmationMessages,
  Constants,
  TitleDefinitionConstants,
  UserRoles,
} from "constants/constant";
import { useAppState } from "context/appState.context";
import NzcDataGrid from "components/NzcDataGrid/NzcDataGrid";
import { GridColDef as GridColDefPro } from "@mui/x-data-grid-pro";
import { Utilities, formatEmissionValue } from "utilities/utilities";
import {
  getFriendlyErrorMessage,
  reportUiError,
} from "services/ui-error-reporter";

const defaultGHGTarget: GHGTarget = {
  order: 0,
  targetNumber: 0,
  locationID: 0,
  targetName: "",
  // No hard-coded base year: the baseline is resolved from the location.
  baseYear: 0,
  targetYear: Constants.TargetYear,
  scopesCovered: "",
  scope1Reduction: "0",
  scope2Reduction: "0",
  scope3GHGReduction: null,
};

/**
 * Custom column menu component to hide certain menu items.
 * @param {GridColumnMenuProps} props - The properties passed to the column menu.
 * @returns {JSX.Element} Custom column menu.
 */

const CustomColumnMenu = (props: GridColumnMenuProps) => {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        // Hide `columnMenuColumnsItem`
        columnMenuColumnsItem: null,
      }}
    />
  );
};

const TargetForm = () => {
  const { userRole } = useAppState();
  // States
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gHGTargetsData, setGHGTargetsData] = useState<
    Array<GHGTargetGridProps>
  >([]);
  const [targetForm, setTargetForm] = useState<GHGTarget>(defaultGHGTarget);
  const [category, setCategory] =
    useState<Scope3GHGReductionModel[]>(initialCategories);
  const [success, setSuccess] = useState<AlertsProps>({
    show: false,
    severity: "info",
    message: "",
  });
  const [openModal, setOpenModal] = useState(false);
  const [targetNumber, setTargetNumber] = useState(0);
  const [targetName, setTargetName] = useState("");
  const [pageErrorMessage, setPageErrorMessage] = useState<string | null>(null);
  const [resolvedBaselineYear, setResolvedBaselineYear] = useState<number | undefined>(
    undefined
  );
  const [growthEndYear, setGrowthEndYear] = useState<number | null>(null);
  const defaultPinnedColumn: GridPinnedColumnFields = {
    right: ["actions"],
  };

  // Params
  const { LocationId } = useParams();

  /**
   * Hides the dialog.
   */
  const hideDialogHandler = () => setDialogOpen(false);

  /**
   * Handles closing the Snackbar.
   * @param {AlertsProps} state - The state of the Snackbar.
   */
  const handleCloseSnackBar = (state: AlertsProps) => {
    setSuccess(state);
  };

  /**
   * Fetches custom GHG targets by location ID.
   */
  const getCustomGHGTargetsByLocationsId = async () => {
    try {
      setLoading(true);
      setPageErrorMessage(null);
      const res = await _getCustomGHGTargetsByLocationsId(Number(LocationId));
      if (res) {
        setGHGTargetsData(res);
      }
    } catch (err) {
      const message = getFriendlyErrorMessage(
        err,
        "We could not load target forms right now. Please refresh and try again."
      );
      setPageErrorMessage(message);
      reportUiError(err, {
        feature: "TargetForm",
        action: "load targets",
        component: "TargetForm",
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
        extra: {
          locationId: LocationId,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  // The baseline and growth-end year both come from the location, so resolve them in one fetch.
  const resolveLocationYears = async () => {
    try {
      const location = await _getLocation(Number(LocationId));
      setResolvedBaselineYear(location?.resolvedBaselineYear ?? undefined);
      const parsedGrowthEndYear = Number(location?.targetYear);
      setGrowthEndYear(Number.isFinite(parsedGrowthEndYear) ? parsedGrowthEndYear : null);
    } catch (err) {
      setGrowthEndYear(null);
      reportUiError(err, {
        feature: "TargetForm",
        action: "resolve location years",
        component: "TargetForm",
        extra: { locationId: LocationId },
      });
    }
  };

  /**
   * Fetches GHG target data by target number.
   * A GHG (Greenhouse Gas) Target refers to a specific goal
   * set by a location to reduce its greenhouse gas emissions over a set period
   * @param {number} targetNumber - The target number.
   */
  const getGHGTargetsByTargetNumber = async (targetNumber: number) => {
    try {
      setLoading(true);
      setPageErrorMessage(null);
      const res = await _getGHGTargetsByTargetNumber(targetNumber);
      if (res) {
        const targetWithDefaultBaseYear: GHGTarget = {
          ...res,
          baseYear: res.baseYear ?? defaultGHGTarget.baseYear,
          targetYear: res.targetYear ?? defaultGHGTarget.targetYear,
        };
        setTargetForm(targetWithDefaultBaseYear);
        setDialogOpen(true);
        setDialogTitle("Edit Target Form");
      }
    } catch (err) {
      const message = getFriendlyErrorMessage(
        err,
        "We could not load this target form right now. Please try again."
      );
      setPageErrorMessage(message);
      reportUiError(err, {
        feature: "TargetForm",
        action: "load target for edit",
        component: "TargetForm",
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
        extra: {
          targetNumber,
          locationId: LocationId,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  // Effect
  useEffect(() => {
    getCustomGHGTargetsByLocationsId();
    resolveLocationYears();
  }, []);

  const shouldShowDashboardYearConstraint =
    growthEndYear !== null &&
    gHGTargetsData.some((target) => Number(target?.targetYear) > growthEndYear);

  /**
   * Handles editing of a GHG target.
   * @param {number} targetNumber - The target number.
   */
  const handleEdit = (targetNumber: number) => {
    getGHGTargetsByTargetNumber(targetNumber);
  };

  /**
   * Renders a tooltip cell with truncated content.
   * @param {any} params - The cell parameters.
   * @returns {JSX.Element | string} Tooltip cell or content.
   */
  const renderTooltipCell = (params: any) => {
    const content = params.value ?? ""; // Handle possible null or undefined values
    const maxLength = 50; // Maximum length before truncating and showing tooltip
    const parts = content.split(",");
    return content.length > maxLength ? (
      <Tooltip
        title={
          <ul>
            {parts.map((part: any, index: number) => (
              <li key={part.trim()}>{part.trim()}</li>
            ))}
          </ul>
        }
      >
        <div
          style={{
            maxWidth: "100%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {content.slice(0, maxLength) + "..."}
        </div>
      </Tooltip>
    ) : (
      content
    );
  };

  /**
   * Opens the dialog.
   */
  const openDialog = () => {
    setOpenModal(true);
  };

  /**
   * Closes the dialog.
   */
  const handleClose = () => {
    setOpenModal(false);
  };

  /**
   * Handles delete target click event.
   * @param {number} targetNumber - The target number.
   * @param {string} targetName - The target name.
   */
  const handleDeleteClick = (targetNumber: number, targetName: string) => {
    openDialog();
    setTargetNumber(targetNumber);
    setTargetName(targetName);
  };

  /**
   * Deletes a GHG(Greenhouse Gas) target.
   */
  const deleteGHGTarget = async () => {
    try {
      const res = await _deleteGHGTarget(targetNumber);
      if (res) {
        setPageErrorMessage(null);
        getCustomGHGTargetsByLocationsId();
      }
    } catch (err) {
      const message = getFriendlyErrorMessage(
        err,
        "We could not delete this target form right now. Please try again."
      );
      setPageErrorMessage(message);
      reportUiError(err, {
        feature: "TargetForm",
        action: "delete target",
        component: "TargetForm",
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
        extra: {
          targetNumber,
          targetName,
          locationId: LocationId,
        },
      });
    }
  };

  const parsedLocationId = LocationId !== undefined ? Number(LocationId) : NaN;
  const isLocationIdValid = !isNaN(parsedLocationId) && parsedLocationId > 0;

  /**
   * Downloads the GHG Target template for the current location.
   */
  const handleExportTemplate = async () => {
    if (!isLocationIdValid) {
      setSuccess({
        show: true,
        severity: "error",
        message: "Invalid or missing Location ID. Cannot download template.",
      });
      return;
    }
    try {
      setPageErrorMessage(null);
      const blob = await _downloadGHGTargetTemplate(parsedLocationId);
      if (blob) {
        const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
        Utilities.triggerFileDownload(blob, `GHGTargets_Location_${parsedLocationId}_${today}.xlsx`);
      }
    } catch (err) {
      const message = getFriendlyErrorMessage(
        err,
        "Failed to download template. Please try again."
      );
      setPageErrorMessage(message);
      reportUiError(err, {
        feature: "TargetForm",
        action: "download target template",
        component: "TargetForm",
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
        extra: {
          locationId: parsedLocationId,
        },
      });
    }
  };

  /**
   * Handles confirmation of deletion.
   */
  const handleConfirm = () => {
    deleteGHGTarget();
    handleClose();
    handleCloseSnackBar({
      show: true,
      severity: "success",
      message: "Deleted Successfully !",
    });
  };

  

  const columns: GridColDefPro[] = [
    {
      field: "targetName",
      headerClassName: "table-header",
      headerName: "Target Name",
      flex: 0.8,
      width: 150,
      minWidth:150,
    },
    {
      field: "baseYear",
      headerClassName: "table-header",
      headerName: "Baseline Year",
      flex: 0.25,
      width: 150,
      minWidth:100
    },
    {
      field: "targetYear",
      headerClassName: "table-header",
      headerName: "Target Year",
      flex: 0.25,
      width: 250,
      minWidth:100
    },
    {
      field: "scopesCovered",
      headerClassName: "table-header",
      headerName: "Scopes Covered",
      flex: 0.25,
      width: 250,
      minWidth:150
    },
    {
      field: "scope3Categories",
      headerClassName: "table-header",
      headerName: "Scope 3 Categories",
      flex: 0.65,
      renderCell: renderTooltipCell,
    },
    {
      field: "baselineValue",
      headerClassName: "table-header",
      headerName: "Baseline Value",
      flex: 0.3,
      renderCell: (params: any) => (
        <div style={{ textAlign: "center", width: "100%" }}>
          {formatEmissionValue(params.value)}
        </div>
      ),
    },
    {
      field: "reduction",
      headerClassName: "table-header",
      headerName: "% Reduction",
      flex: 0.3,
      valueFormatter: (value: any) => `${value} %`,
    },
    {
      field: "actions",
      headerClassName: "table-header",
      headerName: "Actions",
      flex: 0.2,
      width: 150,
      minWidth:150,
      filterable: false,
      renderCell: (params: any) => (
        <div>
          <span title="Edit Target" style={{ cursor: "pointer" }}>
            <EditIcon
              style={{ color: "#e4610f" }}
              onClick={() => handleEdit(params.row.targetNumber)}
              data-testid="editconTestId"
            />
          </span>
          <span title="Delete Target" style={{ cursor: "pointer" }}>
            <DeleteOutlineIcon
              onClick={() =>
                handleDeleteClick(
                  params.row.targetNumber,
                  params.row.targetName
                )
              }
              data-testid="deleteIconTestId"
              style={{
                color: "#e4610f",
                cursor: "pointer",
              }}
            />
          </span>
        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  return (
    <React.Fragment>
      <Box sx={{ mt: 2, mr: 2 }}>
        {shouldShowDashboardYearConstraint && (
          <GuidanceBanner
            sx={{ mb: 2 }}
            title="Dashboard Year Constraint"
            message="If Growth Rate ends before the target end year, the dashboard will only display up to the Growth Rate year."
          />
        )}
        {pageErrorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="target-page-error-alert">
            {pageErrorMessage}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={7}>
            <Typography
              fontSize="14px"
              fontFamily={"Inter"}
              fontWeight={600}
              data-testid="GHGTargetsLabel"
            >
              GHG Targets
            </Typography>
          </Grid>
          <Grid item xs={12} md={5} textAlign={"right"} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
            <Tooltip title={isLocationIdValid ? "Export Template" : "Export unavailable: invalid location"}>
              <span>
              <Button
                variant="outlined"
                aria-label="Export Template"
                data-testid="ExportTemplateButton"
                onClick={handleExportTemplate}
                disabled={!isLocationIdValid}
                sx={{ fontSize: "12px", fontFamily: "Inter", fontWeight: 200, minWidth: 0, px: 1 }}
              >
                <FileDownloadOutlinedIcon fontSize="small" />
              </Button>
              </span>
            </Tooltip>
            {userRole !== UserRoles.Viewers && (
              <Button
                variant="contained"
                data-testid="AddNewTargetButton"
                onClick={() => {
                  setDialogOpen(true);
                  setDialogTitle("Add New Target Form");
                  setTargetForm(defaultGHGTarget);
                  setCategory(initialCategories);
                }}
                sx={{ fontSize: "12px", fontFamily: "Inter", fontWeight: 200 }}
              >
                Add New Target
              </Button>
            )}
          </Grid>
        </Grid>

        <Card variant="outlined" sx={{ overflow: 'hidden', width: '100%' }}>
          <Grid container sx={{ mt: 0, mb: 0, ml: 0, mr: 0, overflow: 'hidden', width: '100%', display: 'flex' }}>
            <Grid sx={{
              overflow: 'hidden',
              transform: 'scale(0.85)',
              transformOrigin: 'top left',
              width: 'calc(100% / 0.85)',
              flex: '0 0 calc(100% / 0.85)',
            }}>
              {loading ? (
                <Spinner size={80} data-testid="spinner" />
              ) : (
                <>
                  {gHGTargetsData.length > 0 ? (
                    <NzcDataGrid 
                      rows={gHGTargetsData}
                      columns={columns}
                      sx={{ width: '100%', minWidth: 0 }}
                      disableRowSelectionOnClick ={true}
                      getRowId={(row) => row?.targetNumber}
                      userRoleParam={UserRoles.Viewers}
                      checkBoxSelection={false}
                      enableFiltering={true}
                      quickFilter={true}
                      columnVisibility={{
                        id: false,
                      }}
                      pinnedColumns={defaultPinnedColumn}
                      hidePagination={false}
                      pagination={true}
                    />
                  ) : (
                    <Grid item xs={11} style={{ textAlign: "center" }}>
                      <Typography
                        variant="h6"
                        id="test-typography-01"
                        component="div"
                        color="gray"
                        style={{ fontWeight: "bold" }}
                        sx={{ mt: 15, mb: 20, ml: 20 }}
                      >
                        {TitleDefinitionConstants.ghgTargetRowsLabel}
                      </Typography>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Card>
      </Box>

      <FormDialog
        dialogOpen={dialogOpen}
        hideDialogHandler={hideDialogHandler}
        dialogTitle={dialogTitle}
        maxWidth="lg"
        isFullScreenAllowed={true}
        childComponent={
          <AddTargetForm
            handleCloseSnackBar={handleCloseSnackBar}
            hideDialogHandler={hideDialogHandler}
            getCustomGHGTargetsByLocationsId={getCustomGHGTargetsByLocationsId}
            defaultGHGTarget={targetForm}
            initialCategories={category}
            resolvedBaselineYear={resolvedBaselineYear}
          ></AddTargetForm>
        }
      ></FormDialog>

      <SnackBar
        show={success.show}
        message={success.message}
        severity={success.severity}
        onclose={() =>
          handleCloseSnackBar({ show: false, severity: "info", message: "" })
        }
      />
      <AlertDialog
        open={openModal}
        openDialog={openDialog}
        handleClose={handleClose}
        handleConfirm={handleConfirm}
        locationName={targetName}
        confirmationMessage={ConfirmationMessages.ghgConfirmation}
      />
    </React.Fragment>
  );
};

export default TargetForm;
